import { EventEmitter, Injectable } from '@angular/core';
import * as signalR from "@microsoft/signalr"
import { ApiUrls } from '../../constants/constants';
import { environment } from 'src/environment/environment';
import * as signalRConstants from '../../constants/signalrMessages';
import { UserDataService } from '../userData.service';
import { AssetAssistance, AssetComment, AssetParticipants } from '../../interfaces/AssetInfo';
import { ImageReceived } from '../../interfaces/imageReceived';
import { ModifiedObjectsEmitterData } from 'src/app/interfaces/SignalRDrawObject';
import { StartAudioConnectorResponse } from '../../interfaces/Responses/AudioConnectorResponse';
import { CallNotificationInfo, CallNotificationResponse } from 'src/app/interfaces/call-notifications';
import { ShowPointerResponse } from '../../interfaces/Responses/ShowPointerResponse';
import { chatMessageWithFile } from 'src/app/models/chatMessageResponse';
import { updateUserName } from '../../models/updateUserName';
import { AssistanceUpdateResponse } from '../../interfaces/Responses/AssistanceStateUpdateResponse';
import { Router } from '@angular/router';
import { SecurityService } from '../security.service';


@Injectable({
  providedIn: 'root'
})

export class SignalRService {

  private hubConnection: signalR.HubConnection;

  private connectedPromise!: Promise<void>;
  private resolveConnected!: () => void;

  onAssetDeleted = new EventEmitter<string>();
  onUpdateParticipant = new EventEmitter<any>();
  onImageReceived = new EventEmitter<ImageReceived>();
  onCanvasObjectReceived = new EventEmitter<ModifiedObjectsEmitterData>();
  OnLiveDrawingClose = new EventEmitter<void>();
  onRemoteRecording = new EventEmitter();
  onHDImageRequest = new EventEmitter<string>();
  onChatMessageReceived = new EventEmitter<chatMessageWithFile>();
  onPublishTranslatedAudio = new EventEmitter<string>();
  onStartAudioConnector = new EventEmitter<StartAudioConnectorResponse>();
  onTranscriptionReady = new EventEmitter<string>();
  onSubtitleReady = new EventEmitter<string>();
  onStopAudioConnector = new EventEmitter<string>();
  onCallNotification = new EventEmitter<CallNotificationInfo>();
  onCallNotificationResponse = new EventEmitter<CallNotificationResponse>();
  onReceivePointer = new EventEmitter<ShowPointerResponse>();
  onDeleteChatMessage = new EventEmitter<number>();
  onAssistanceUpdate = new EventEmitter<AssistanceUpdateResponse>();
  onAddAssistance = new EventEmitter<AssetAssistance>();
  onAddCommentAsset = new EventEmitter<AssetComment>();
  onDeleteCommentAsset = new EventEmitter<number>();
  onRestoreChat = new EventEmitter();
  onReceiveUploadProgress = new EventEmitter<any>();

  constructor(private userDataService: UserDataService, private router: Router, private securityService: SecurityService) {
    this.hubConnection = new signalR.HubConnectionBuilder()
      .withUrl(environment.apiUrl + ApiUrls.signalRHub) //,{skipNegotiation : false  , transport : signalR.HttpTransportType.WebSockets})
      .configureLogging(signalR.LogLevel.Information)
      .withAutomaticReconnect()
      .build();

    this.hubConnection.onclose(async (error) => {
      console.log('Conexion cerrada', error);
    });

    this.hubConnection.onreconnected((connectionId) => {
      console.log('Conexion restaurada: ', connectionId);
      this.sendSignalRConnectionData(userDataService.Id!, userDataService.AssetId!, userDataService.Name!, userDataService.Role!); //add user to asset again
      this.onRestoreChat.emit();
      this.router.navigate(['waitingasset']);
    });

    this.hubConnection.onreconnecting((error) => {
      console.log('Intentando reconectar...', error);    
      this.router.navigate(['/reconnect']);
    });

    this.connectedPromise = new Promise(resolve => {
      this.resolveConnected = resolve;
    });
  }

  async connect(): Promise<void> {
    if (this.hubConnection.state === signalR.HubConnectionState.Connected) return;

    try {
      await this.hubConnection.start();
      console.log('SignalR conectado');
      this.registerEvents();
      this.resolveConnected();
    } catch (err) {
      console.error('Error al conectar SignalR', err);
      setTimeout(() => this.connect(), 3000);
    }
  }

  async ensureConnected(): Promise<void> {
    return await this.connectedPromise;
  }

  sendSignalRConnectionData(userId: Number, assetId: number, userName: string, role: number) {
    console.log("Sending sr connection data");
    this.hubConnection?.invoke(signalRConstants.ConnectionData, userId, assetId, userName, role, navigator.userAgent)
      .then(response => {
        console.log("Response to : " + signalRConstants.ConnectionData + " => " + response);
      })
      .catch(error => {
        console.log("Error on  : " + signalRConstants.ConnectionData + " => " + error);
      });

  }

  liveDrawingClose(callId: number, userId: number) {
    console.log("Sending live drawing close connection data");
    this.hubConnection?.invoke(signalRConstants.LiveDrawingClose, userId, callId)
      .then(response => {
        console.log("Response to : " + signalRConstants.LiveDrawingClose + " => " + response);
      })
      .catch(error => {
        console.log("Error on  : " + signalRConstants.LiveDrawingClose + " => " + error);
      });
  }

  setTranslateSubtitles(assetId: number, callId: number, value: boolean) {
    console.log("Sending translation subtitles ", value);
    this.hubConnection?.invoke(signalRConstants.setTranslationSubtitles, assetId, callId, value)
      .then(response => {
        console.log("Response to : " + signalRConstants.setTranslationSubtitles + " => " + response);
      })
      .catch(error => {
        console.log("Error on  : " + signalRConstants.setTranslationSubtitles + " => " + error);
      });
  }

  setTranslateChat(assetId: number, value: boolean) {
    console.log("Sending enable chat", value);
    this.hubConnection?.invoke(signalRConstants.setChatTranslation, assetId, value)
      .then(response => {
        console.log("Response to : " + signalRConstants.setChatTranslation + " => " + response);
      })
      .catch(error => {
        console.log("Error on  : " + signalRConstants.setChatTranslation + " => " + error);
      });
  }

  updateTranslationLang(assetId: number, userId: number, lang: string, from: boolean) {
    console.log("Update lang", userId, lang, from);
    this.hubConnection?.invoke(signalRConstants.updateTranslationLang, assetId, userId, lang, from)
      .then(response => {
        console.log("Response to : " + signalRConstants.updateTranslationLang + " => " + response);
      })
      .catch(error => {
        console.log("Error on  : " + signalRConstants.updateTranslationLang + " => " + error);
      });
  }

  updateMicrophone(assetId: number, callId: number, participantId: number, active: boolean) {
    console.log("Update microphone", participantId, active);
    this.hubConnection?.invoke(signalRConstants.updateMicrophone, assetId, callId, participantId, active)
      .then(response => {
        console.log("Response to : " + signalRConstants.updateMicrophone + " => " + response);
      })
      .catch(error => {
        console.log("Error on  : " + signalRConstants.updateMicrophone + " => " + error);
      });
  }

  updateTranscriptions(assetId: number, value: boolean) {
    console.log("Sending transcriptions", value);
    this.hubConnection?.invoke(signalRConstants.updateTranscriptions, assetId, value)
      .then(response => {
        console.log("Response to : " + signalRConstants.updateTranscriptions + " => " + response);
      })
      .catch(error => {
        console.log("Error on  : " + signalRConstants.updateTranscriptions + " => " + error);
      });
  }

  updateTranscriptionsLang(assetId: number, value: string | undefined) {
    console.log("Sending transcriptions", value);
    this.hubConnection?.invoke(signalRConstants.updateTranscriptionsLang, assetId, value)
      .then(response => {
        console.log("Response to : " + signalRConstants.updateTranscriptionsLang + " => " + response);
      })
      .catch(error => {
        console.log("Error on  : " + signalRConstants.updateTranscriptionsLang + " => " + error);
      });
  }

  sendPointer(callId: number, userId: number, posX: number, posY: number) {
    this.hubConnection?.invoke(signalRConstants.sendPointer, callId, userId, posX, posY)
      .then(response => {
        console.log("Response to : " + signalRConstants.sendPointer + " => " + response);
      })
      .catch(error => {
        console.log("Error on  : " + signalRConstants.sendPointer + " => " + error);
      });
  }

  startTranscription(sessionId: string, callId: number, streamId: string, userConnectionId: string, langFrom: string) {
    return this.hubConnection?.invoke(signalRConstants.startTranscription, sessionId, callId, streamId, userConnectionId, langFrom);
  }

  startTranslation(sessionId: string, callId: number, lang: string, streamId: string, userConnectionId: string, langFrom: string) {
    return this.hubConnection?.invoke(signalRConstants.startTranslation, sessionId, callId, streamId, userConnectionId, lang, langFrom);
  }

  stopTranslations(sessionId: string, callId: number, audioConnectorStreamId: string, userConnectionId: string) {
    return this.hubConnection?.invoke(signalRConstants.stopTranslation, sessionId, callId, audioConnectorStreamId, userConnectionId);
  }

  reportUploadProgress(assetId: number, messageId: string, progress: number) {
    this.hubConnection?.invoke(signalRConstants.reportUploadProgress, assetId, messageId, progress)
      .then(response => {
        console.log("Response to : " + signalRConstants.reportUploadProgress + " => " + response);
      })
      .catch(error => {
        console.log("Error on  : " + signalRConstants.reportUploadProgress + " => " + error);
      });
  }

  registerEvents() {
    this.hubConnection.on(signalRConstants.AssetDeleted, data => this.onAssetDeleted.emit(data));
    this.hubConnection.on(signalRConstants.ImageReceived, data => this.imageReceived(data));
    this.hubConnection.on(signalRConstants.CanvasObject, data => this.canvasObjectReceived(data));
    this.hubConnection.on(signalRConstants.LiveDrawingClose, data => this.liveDrawingCloseReceived(data));
    this.hubConnection.on(signalRConstants.RemoteRecording, data => this.remoteRecording(data));
    this.hubConnection.on(signalRConstants.requestHDImage, data => this.requestedHDImage(data));
    this.hubConnection.on(signalRConstants.chatMessage, data => this.chatMessageReceived(data));
    this.hubConnection.on(signalRConstants.setOpenTokConnectionId, data => this.setOpenTokConnectionId(data));
    this.hubConnection.on(signalRConstants.audioTranslationReady, data => this.audioTranslationReady(data));
    this.hubConnection.on(signalRConstants.audioTranscriptionReady, data => this.audioTranscriptionReady(data));
    this.hubConnection.on(signalRConstants.audioSubtitleReady, data => this.audioSubtitleReady(data));
    this.hubConnection.on(signalRConstants.audioTranslationStop, data => this.audioTranslationStop(data));
    this.hubConnection.on(signalRConstants.setTranslationSubtitles, data => this.setTranslationSubtitles(data));
    this.hubConnection.on(signalRConstants.setChatTranslation, data => this.setChatTranslation(data));
    this.hubConnection.on(signalRConstants.setTranslationLanguages, data => this.setTranslationLanguages(data));
    this.hubConnection.on(signalRConstants.CallNotification, data => this.callNotification(data));
    this.hubConnection.on(signalRConstants.CallNotificationResponse, data => this.callNotificationResponse(data));
    this.hubConnection.on(signalRConstants.showPointer, data => this.showPointer(data));
    this.hubConnection.on(signalRConstants.initParticipant, data => this.initParticipantReceived(data));
    this.hubConnection.on(signalRConstants.addParticipant, data => this.addParticipantReceived(data));
    this.hubConnection.on(signalRConstants.disconnectParticipant, data => this.disconnectParticipantReceived(data));
    this.hubConnection.on(signalRConstants.setMicrophone, data => this.setMicrophone(data));
    this.hubConnection.on(signalRConstants.setTranscriptions, data => this.setTranscriptions(data));
    this.hubConnection.on(signalRConstants.setTranscriptionsLang, data => this.setTranscriptionsLang(data));
    this.hubConnection.on(signalRConstants.setConnectionId, data => this.setConnectionId(data));
    this.hubConnection.on(signalRConstants.deleteChatMessage, data => this.deleteChatMessage(data));
    this.hubConnection.on(signalRConstants.updateUserName, data => this.updateUserName(data));
    this.hubConnection.on(signalRConstants.updateAssistanceState, data => this.updateAssistanceState(data));
    this.hubConnection.on(signalRConstants.updateParticipants, data => this.updateParticipants(data));
    this.hubConnection.on(signalRConstants.addAssistance, data => this.addAssistance(data));
    this.hubConnection.on(signalRConstants.addCommentAsset, data => this.addCommentAsset(data));
    this.hubConnection.on(signalRConstants.deleteCommentAsset, data => this.deleteCommentAsset(data));
    this.hubConnection.on(signalRConstants.receiveUploadProgress, data => this.receiveUploadProgress(data));
  }

  public unRegisterEvents() {
    console.log("SignalR unregister events");
    this.hubConnection.off(signalRConstants.AssetDeleted);
    this.hubConnection.off(signalRConstants.ImageReceived);
    this.hubConnection.off(signalRConstants.CanvasObject);
    this.hubConnection.off(signalRConstants.LiveDrawingClose);
    this.hubConnection.off(signalRConstants.RemoteRecording);
    this.hubConnection.off(signalRConstants.requestHDImage);
    this.hubConnection.off(signalRConstants.chatMessage);
    this.hubConnection.off(signalRConstants.setOpenTokConnectionId);
    this.hubConnection.off(signalRConstants.audioTranslationReady);
    this.hubConnection.off(signalRConstants.audioTranscriptionReady);
    this.hubConnection.off(signalRConstants.audioSubtitleReady);
    this.hubConnection.off(signalRConstants.audioTranslationStop);
    this.hubConnection.off(signalRConstants.setTranslationSubtitles);
    this.hubConnection.off(signalRConstants.setChatTranslation);
    this.hubConnection.off(signalRConstants.setTranslationLanguages);
    this.hubConnection.off(signalRConstants.CallNotification);
    this.hubConnection.off(signalRConstants.CallNotificationResponse);
    this.hubConnection.off(signalRConstants.showPointer);
    this.hubConnection.off(signalRConstants.addParticipant);
    this.hubConnection.off(signalRConstants.disconnectParticipant);
    this.hubConnection.off(signalRConstants.setMicrophone);
    this.hubConnection.off(signalRConstants.setConnectionId);
    this.hubConnection.off(signalRConstants.deleteChatMessage);
    this.hubConnection.off(signalRConstants.updateUserName);
    this.hubConnection.off(signalRConstants.updateAssistanceState);
    this.hubConnection.off(signalRConstants.addAssistance);
    this.hubConnection.off(signalRConstants.addCommentAsset);
    this.hubConnection.off(signalRConstants.deleteCommentAsset);
    this.hubConnection.off(signalRConstants.receiveUploadProgress);
  }


  imageReceived(data: any): any {
    console.log("Image received, is live: " + data.isLiveImage);
    let imageReceived: ImageReceived = { base64Image: data.base64Image, isLiveDrawing: data.isLiveImage, isHdImage: data.isHdImage };
    this.onImageReceived.emit(imageReceived);
  }

  initParticipantReceived(data: any): any {
    console.log("*initParticipantReceived: ", data);
    this.userDataService.setParticipantId(data.participantId);
    this.addParticipantReceived(data);
  }

  addParticipantReceived(data: any): any {
    console.log("*addParticipantReceived: ", data);

    if (this.userDataService.assetInfo == undefined) return;

    var index = this.userDataService.assetInfo!.participants.findIndex(p => p.participantId == data.participantId);
    if (index == -1) this.userDataService.assetInfo!.participants.push(data);
    else this.userDataService.assetInfo!.participants[index] = data;

    this.onUpdateParticipant.emit(data.participantId);
  }

  disconnectParticipantReceived(participantId: number): any {
    //if (this.hubConnection.state === signalR.HubConnectionState.Connected) {
    //  console.log("hub connected - desconexión fantasma");
    //  if (participantId == this.userDataService.ParticipantId) {
    //    this.hubConnection.stop();
    //    var token = this.securityService.getUrlToken();
    //    if (token === null) {
    //      this.router.navigate(['']);
    //    }
    //    else {
    //      this.router.navigate([`/${token}`]);
    //    }
    //  }
    //}

    var index = this.userDataService.assetInfo!.participants.findIndex(p => p.participantId == participantId);
    console.log("*disconnectParticipantReceived: ", participantId);
    if (index == -1) return;

    this.userDataService.assetInfo!.participants.splice(index, 1);
    this.onUpdateParticipant.emit(participantId);
  }

  getConnectionId(): string | null {
    return this.hubConnection.connectionId;
  }

  SendCanvasObject(userId: number, callId: number, object: ModifiedObjectsEmitterData) {
    console.log("Sending CanvasObject connection data");
    this.hubConnection?.invoke(signalRConstants.CanvasObject, userId, callId, object)
      .then(response => {
        console.log("Response to : " + signalRConstants.CanvasObject + " => " + response);
      })
      .catch(error => {
        console.log("Error on  : " + signalRConstants.CanvasObject + " => " + error);
      });
  }

  canvasObjectReceived(data: ModifiedObjectsEmitterData): any {
    this.onCanvasObjectReceived.emit(data);
  }

  liveDrawingCloseReceived(data: any): any {
    this.OnLiveDrawingClose.emit();
  }

  remoteRecording(data: any): any {
    this.onRemoteRecording.emit(data);
  }

  requestedHDImage(senderSignalRId: string) {
    console.log("HD image requested");
    this.onHDImageRequest.emit(senderSignalRId);
  }

  chatMessageReceived(data: chatMessageWithFile) {
    data.chatData.date = new Date(data.chatData.date);
    this.onChatMessageReceived.emit(data);
  }

  setOpenTokConnectionId(data: StartAudioConnectorResponse) {
    this.onStartAudioConnector.emit(data);
  }

  audioTranslationReady(data: string) {
    this.onPublishTranslatedAudio.emit(data);
  }

  audioTranscriptionReady(data: string) {
    this.onTranscriptionReady.emit(data);
  }

  audioSubtitleReady(data: string) {
    this.onSubtitleReady.emit(data);
  }

  audioTranslationStop(data: string) {
    this.onStopAudioConnector.emit(data);
  }

  setTranslationSubtitles(data: any): any {
    console.log("setTranslationSubtitles: ", data);
    this.userDataService.assetInfo!.participants.forEach(participant => participant.translateSubtitleOn = data);
  }

  setChatTranslation(data: any): any {
    console.log("setTranslateChat: ", data);
    this.userDataService.assetInfo!.participants.forEach(participant => participant.translateChatOn = data);
  }

  setTranscriptions(data: any): any {
    console.log("updateTranscriptions: ", data);
    this.userDataService.assetInfo!.transcriptionsOn = data;
  }

  setTranscriptionsLang(data: any): any {
    console.log("updateTranscriptionLangs: ", data);
    this.userDataService.assetInfo!.transcriptionsLanguage = data;
  }

  setTranslationLanguages(data: any): any {
    console.log("setTranslationLanguages: ", data);

    var user = this.userDataService.assetInfo!.participants.find(participant => participant.participantId == data.participantId);
    if (user == undefined) return;

    user.originLanguage = data.fromLanguage;
    user.translationLanguage = data.toLanguage;

    this.onUpdateParticipant.emit(data.participantId);
  }

  callNotification(data: CallNotificationInfo) {
    console.log("Incoming call notification", JSON.stringify(data));
    this.onCallNotification.emit(data);
  }

  callNotificationResponse(result: CallNotificationResponse): any {
    console.log("Incoming call notification Response", result);
    this.onCallNotificationResponse.emit(result);
  }

  showPointer(data: ShowPointerResponse) {
    console.log("**ShowPointer event back", data);
    this.onReceivePointer.emit(data);
  }

  setMicrophone(data: any) {
    console.log("setMicrophone: ", data);

    var user = this.userDataService.assetInfo!.participants.find(participant => participant.participantId == data.participantId);
    if (user == undefined) return;

    user.microphone = data.value;

    this.onUpdateParticipant.emit(data.participantId);
  }

  setConnectionId(data: any) {
    console.log("setConnectionId: ", data);
    var user = this.userDataService.assetInfo!.participants.find(participant => participant.participantId == data.participantId);
    if (user == undefined) return;
    user.connectionId = data.value;
    this.onUpdateParticipant.emit(data.participantId);
  }

  deleteChatMessage(data: any) {
    console.log("signalr deleteChatMessage: ", data);
    this.onDeleteChatMessage.emit(data);
  }

  updateUserName(data: updateUserName) {
    console.log("UpdateUserData", data, this.userDataService.assetInfo.participants);
    var participantToUpdate = this.userDataService.assetInfo.participants.find(participant => participant.participantId == data.participantId) as AssetParticipants;
    console.log("UpdateUserData participant to update", participantToUpdate);
    participantToUpdate.name = data.userName;

    if (this.userDataService.ParticipantId == participantToUpdate.participantId) this.userDataService.Name = participantToUpdate.name;
  }

  updateParticipants(data: AssetParticipants[]) {
    console.log("updateParticipants", this.userDataService.assetInfo.participants);

    data.forEach(participant => {
      console.log("participant", participant.id, participant.participantId, participant.name, participant.callId);
      var index = this.userDataService.assetInfo.participants.findIndex(user => user.participantId == participant.participantId);
      if (index >= 0) {
        console.log("participant update");
        this.userDataService.assetInfo.participants[index] = participant;
        if (participant.participantId == this.userDataService.ParticipantId) {
          console.log("update principal");
          this.onUpdateParticipant.emit(this.userDataService.ParticipantId);
        }
      }

    });
  }

  updateAssistanceState(data: AssistanceUpdateResponse) {
    this.onAssistanceUpdate.emit(data);
  }

  addAssistance(data: AssetAssistance) {
    this.onAddAssistance.emit(data);
  }

  addCommentAsset(data: AssetComment) {
    this.onAddCommentAsset.emit(data);
  }

  deleteCommentAsset(data: number) {
    this.onDeleteCommentAsset.emit(data);
  }

  connectionState(): signalR.HubConnectionState {
    return this.hubConnection.state;
  }

  receiveUploadProgress(data: any) {
    console.log(`Mensaje ${data.messageId} está al ${data.progress}%`);
    this.onReceiveUploadProgress.emit(data);
  }

}
